import React, { useState, useEffect, useCallback } from 'react';
import { Cpu, Send, Trash2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVIDER_META = {
  openai: { label: 'OpenAI', keyUrl: 'https://platform.openai.com/api-keys', placeholder: 'sk-...' },
  gemini: { label: 'Google Gemini', keyUrl: 'https://aistudio.google.com/apikey', placeholder: 'AIza...' },
  claude: { label: 'Anthropic Claude', keyUrl: 'https://console.anthropic.com/settings/keys', placeholder: 'sk-ant-...' },
};

const AISettings = ({ authHeaders, onAuthError }) => {
  const [settings, setSettings] = useState(null);
  const [keyInputs, setKeyInputs] = useState({ openai: '', gemini: '', claude: '' });
  const [models, setModels] = useState({});
  const [activeProvider, setActiveProvider] = useState('openai');
  const [message, setMessage] = useState('');
  const [testMessages, setTestMessages] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState('');
  const [serviceStatus, setServiceStatus] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/service-status`, { headers: authHeaders() });
      setServiceStatus(res.data.services || []);
    } catch (error) {
      if (!onAuthError(error)) console.error('Error loading service status:', error);
    }
    // eslint-disable-next-line
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ai-settings`, { headers: authHeaders() });
      setSettings(res.data);
      setActiveProvider(res.data.active_provider);
      const m = {};
      Object.keys(res.data.providers).forEach((p) => { m[p] = res.data.providers[p].model; });
      setModels(m);
    } catch (error) {
      if (!onAuthError(error)) console.error('Error loading AI settings:', error);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); loadStatus(); }, [load, loadStatus]);

  const buildPayload = () => ({
    active_provider: activeProvider,
    providers: {
      openai: { api_key: keyInputs.openai, model: models.openai },
      gemini: { api_key: keyInputs.gemini, model: models.gemini },
      claude: { api_key: keyInputs.claude, model: models.claude },
    },
  });

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await axios.post(`${API}/admin/ai-settings`, buildPayload(), { headers: authHeaders() });
      setMessage('✅ KI-Einstellungen gespeichert');
      setKeyInputs({ openai: '', gemini: '', claude: '' });
      await load();
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      if (!onAuthError(error)) setMessage('❌ Fehler beim Speichern');
    }
    setSaving(false);
  };

  const handleTest = async (provider) => {
    setTesting(provider);
    setTestMessages((t) => ({ ...t, [provider]: '' }));
    try {
      // Erst speichern, damit frisch eingegebene Keys getestet werden
      await axios.post(`${API}/admin/ai-settings`, buildPayload(), { headers: authHeaders() });
      setKeyInputs({ openai: '', gemini: '', claude: '' });
      await load();
      const res = await axios.post(`${API}/admin/ai-settings/test`, { provider }, { headers: authHeaders() });
      setTestMessages((t) => ({
        ...t,
        [provider]: `${res.data.success ? '✅' : '❌'} ${res.data.message}`,
      }));
      loadStatus();
    } catch (error) {
      if (!onAuthError(error)) setTestMessages((t) => ({ ...t, [provider]: '❌ Test fehlgeschlagen' }));
    }
    setTesting('');
  };

  const handleClearKey = async (provider) => {
    try {
      const payload = buildPayload();
      payload.providers[provider].api_key = '__clear__';
      await axios.post(`${API}/admin/ai-settings`, payload, { headers: authHeaders() });
      setMessage(`✅ Key für ${PROVIDER_META[provider].label} entfernt`);
      await load();
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      if (!onAuthError(error)) setMessage('❌ Fehler beim Entfernen');
    }
  };

  if (!settings) {
    return <div className="bg-white rounded-2xl p-8 text-[#86868B]">Lade KI-Einstellungen...</div>;
  }

  const anyKey = Object.values(settings.providers).some((p) => p.has_key);

  return (
    <div className="space-y-6" data-testid="ai-settings-panel">
      <div className="bg-white rounded-2xl p-8">
        <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-2 flex items-center gap-2">
          <Cpu className="w-6 h-6 text-[#0071E3]" strokeWidth={1.5} />
          KI-Einstellungen
        </h2>
        <p className="text-sm text-[#86868B] mb-6">
          Das KI-Kontaktformular läuft über Ihre eigenen API-Keys. Wählen Sie den aktiven Anbieter –
          fällt dieser aus, wechselt das System automatisch auf die anderen hinterlegten Anbieter.
        </p>

        {!anyKey && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="ai-no-key-warning">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>Kein API-Key hinterlegt.</strong> Das KI-Kontaktformular auf der Webseite ist
              deaktiviert, bis mindestens ein Key eingetragen und gespeichert wurde.
            </p>
          </div>
        )}

        <div className="space-y-5">
          {['openai', 'gemini', 'claude'].map((p) => {
            const info = settings.providers[p];
            const meta = PROVIDER_META[p];
            return (
              <div
                key={p}
                className={`border rounded-2xl p-5 transition-colors ${activeProvider === p ? 'border-[#0071E3] bg-[#0071E3]/5' : 'border-[#D2D2D7]'}`}
                data-testid={`ai-provider-card-${p}`}
              >
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="active_provider"
                      checked={activeProvider === p}
                      onChange={() => setActiveProvider(p)}
                      className="w-4 h-4 accent-[#0071E3]"
                      data-testid={`ai-active-${p}`}
                    />
                    <span className="font-semibold text-[#1D1D1F]">{meta.label}</span>
                    {activeProvider === p && (
                      <span className="text-xs bg-[#0071E3] text-white rounded-full px-2.5 py-0.5">Aktiv</span>
                    )}
                    {info.has_key && (
                      <span className="text-xs text-green-700 bg-green-100 rounded-full px-2.5 py-0.5 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Key gespeichert
                      </span>
                    )}
                  </label>
                  <a
                    href={meta.keyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#0071E3] hover:underline"
                  >
                    Key erstellen →
                  </a>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#86868B] block mb-1">API-Key</label>
                    <div className="flex gap-2">
                      <Input
                        type="password"
                        value={keyInputs[p]}
                        onChange={(e) => setKeyInputs({ ...keyInputs, [p]: e.target.value })}
                        placeholder={info.has_key ? `Gespeichert (${info.key_hint}) – leer lassen zum Behalten` : meta.placeholder}
                        className="bg-[#F5F5F7] border-0 rounded-xl h-11"
                        data-testid={`ai-key-input-${p}`}
                      />
                      {info.has_key && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleClearKey(p)}
                          className="h-11 w-11 p-0 rounded-xl flex-shrink-0"
                          title="Key entfernen"
                          data-testid={`ai-clear-key-${p}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#86868B] block mb-1">Modell</label>
                    <select
                      value={models[p] || ''}
                      onChange={(e) => setModels({ ...models, [p]: e.target.value })}
                      className="w-full h-11 bg-[#F5F5F7] rounded-xl px-3 text-sm text-[#1D1D1F] border-0 focus:ring-1 focus:ring-[#0071E3]"
                      data-testid={`ai-model-select-${p}`}
                    >
                      {(settings.available_models[p] || []).map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTest(p)}
                    disabled={testing !== ''}
                    className="rounded-full h-9 px-4 text-sm"
                    data-testid={`ai-test-${p}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-2" />
                    {testing === p ? 'Teste...' : 'Testen'}
                  </Button>
                  {testMessages[p] && (
                    <span className="text-sm text-[#1D1D1F]" data-testid={`ai-test-result-${p}`}>{testMessages[p]}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {message && (
          <p className="mt-5 text-sm text-[#1D1D1F]" data-testid="ai-settings-message">{message}</p>
        )}

        <Button
          onClick={handleSave}
          disabled={saving}
          className="mt-5 w-full bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-12"
          data-testid="ai-settings-save"
        >
          {saving ? 'Speichere...' : 'Einstellungen speichern'}
        </Button>

        <p className="text-xs text-[#86868B] mt-4">
          Hinweis: Die Keys werden sicher in der Datenbank gespeichert und nie im Browser angezeigt.
          Kosten fallen direkt bei Ihrem jeweiligen Anbieter-Konto an.
        </p>
      </div>

      <div className="bg-white rounded-2xl p-8" data-testid="service-status-panel">
        <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">Dienst-Überwachung</h3>
        <p className="text-sm text-[#86868B] mb-5">
          Alle 30 Minuten werden die hinterlegten KI-Anbieter automatisch geprüft (plus Live-Überwachung bei jeder Anfrage).
          Bei einem Ausfall – und bei der Wiederherstellung – erhalten Sie sofort eine E-Mail an die in den
          E-Mail-Einstellungen hinterlegte Adresse.
        </p>
        {serviceStatus.filter((s) => !s.service.startsWith('_')).length === 0 ? (
          <p className="text-sm text-[#86868B]" data-testid="service-status-empty">
            Noch keine Prüfdaten vorhanden – der erste automatische Check läuft innerhalb von 30 Minuten,
            oder klicken Sie oben bei einem Anbieter auf «Testen».
          </p>
        ) : (
          <div className="space-y-2">
            {serviceStatus.filter((s) => !s.service.startsWith('_')).map((s) => (
              <div
                key={s.service}
                className="flex items-center justify-between bg-[#F5F5F7] rounded-xl px-4 py-3 flex-wrap gap-2"
                data-testid={`service-status-${s.service}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${s.status === 'down' ? 'bg-red-500' : 'bg-green-500'}`} />
                  <span className="text-sm font-medium text-[#1D1D1F]">
                    {{ openai: 'OpenAI', gemini: 'Google Gemini', claude: 'Anthropic Claude', email: 'E-Mail-Versand' }[s.service] || s.service}
                  </span>
                  <span className={`text-xs rounded-full px-2 py-0.5 ${s.status === 'down' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {s.status === 'down' ? 'Ausgefallen' : 'Läuft'}
                  </span>
                </div>
                <span className="text-xs text-[#86868B]">
                  {s.status === 'down' && s.last_error
                    ? `Fehler: ${s.last_error.slice(0, 80)}`
                    : s.last_ok
                      ? `Zuletzt geprüft: ${new Date(s.last_ok).toLocaleString('de-CH')}`
                      : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AISettings;
